import { collapsedContainerClass, selectionValuesForScrolling } from './color-image-tiles/constants';
import { collapsedLinkClass, expandedLinkClass } from './color-image-tiles/constants';
import { ensureSelectedColorIsVisible } from './color-image-tiles/ensure-selected-color-is-visible';
import { makeOnlyVisibleTilesClickable } from './color-image-tiles/make-only-visible-tiles-clickable';
import { restoreExpansionStatus } from './color-image-tiles/restore-expansion-status';
import { setupMoreColorsLink } from './color-image-tiles/setup-more-colors-link';
import { getCurrentMajorBreakpoint, MajorBreakpoint } from '../../_common/client/breakpoint';
import { restoreScrollPosition, storeScrollPositionAfterScrolling } from './scrollable-tiles/scroll-position-for-mobile';
import { trackDimensionSwipes } from './tracking/track-dimension-swipes';
import { scrollSelectedTileIntoView } from './scrollable-tiles/scroll-selected-tile-into-view';

/**
 *
 *
 */
export const setupDimensionTiles = (rootElement: HTMLElement): (() => void) => {
    const storageKey = getStorageKey(rootElement);
    if (storageKey == null) return () => {};

    const observers: ResizeObserver[] = [];
    setupExpandLink(rootElement, storageKey, observers);
    setupMobileScrollHandling(rootElement, storageKey, observers);

    return () => observers.forEach(observer => observer.disconnect());
};

/*                                                                                             */
/*                                                                                           */
function setupMobileScrollHandling(rootElement: HTMLElement, storageKey: string, observers: ResizeObserver[]) {
    let initialisedForCurrentMobilePeriod = false;
    const sync = () => {
        const isMobile = getCurrentMajorBreakpoint() === MajorBreakpoint.Mobile;
        if (isMobile && !initialisedForCurrentMobilePeriod) {
            initialisedForCurrentMobilePeriod = true;
            handleHorizontallyHiddenTilesForAllScrollingContainers(rootElement, storageKey);
        } else if (!isMobile) {
            initialisedForCurrentMobilePeriod = false;
        }
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(document.body);
    observers.push(observer);
}

function setupExpandLink(containerRoot: HTMLElement, storageKey: string, observers: ResizeObserver[]) {
    const showMoreColorsLink = containerRoot.querySelector<HTMLElement>('.js_pdp_dimension-selection__show-more-colors-link');
    if (showMoreColorsLink == null) return;

    /*                                                                                            */
    /*                                                                   */
    const dimensionBlock = showMoreColorsLink.closest<HTMLElement>('[data-dim-type]') ?? containerRoot;
    const imageTilesContainer =
        dimensionBlock.querySelector<HTMLElement>('.js_pdp_dimension-selection__values') ??
        dimensionBlock.querySelector<HTMLElement>(`.${collapsedContainerClass}`);
    if (imageTilesContainer == null) {
        showMoreColorsLink.classList.add('pl_hidden');
        return;
    }

    const tileVariant = imageTilesContainer.dataset.tileVariant;
    const scopedStorageKey = tileVariant ? `${storageKey}-${tileVariant}-image-lane` : storageKey;

    if (tileVariant === 'oc') {
        ensureOcContainerStartsCollapsed(showMoreColorsLink, imageTilesContainer);
    }

    keepExpandLinkInSyncWithOverflow(showMoreColorsLink, imageTilesContainer, scopedStorageKey, observers);
}

/*                                                                                               */
/*                                                                          */
function keepExpandLinkInSyncWithOverflow(
    showMoreColorsLink: HTMLElement,
    imageTilesContainer: HTMLElement,
    scopedStorageKey: string,
    observers: ResizeObserver[]
) {
    let wiredUp = false;

    const refresh = () => {
        /*                                                                                           */
        /*                                                                                         */
        /*                                                                                      */
        const isMobile = getCurrentMajorBreakpoint() === MajorBreakpoint.Mobile;

        if (isMobile || !collapsedLaneHidesTiles(imageTilesContainer)) {
            showMoreColorsLink.classList.add('pl_hidden');
            return;
        }

        if (!wiredUp) {
            wiredUp = true;
            setupMoreColorsLink(showMoreColorsLink, imageTilesContainer, scopedStorageKey);
            ensureSelectedColorIsVisible(imageTilesContainer, showMoreColorsLink, scopedStorageKey);
            restoreExpansionStatus(showMoreColorsLink, imageTilesContainer, scopedStorageKey);
        } else {
            showMoreColorsLink.classList.remove('pl_hidden');
        }
        makeOnlyVisibleTilesClickable(imageTilesContainer);
    };

    refresh();

    const resizeObserver = new ResizeObserver(refresh);
    resizeObserver.observe(imageTilesContainer);
    /*                                                                                            */
    /*                                                  */
    imageTilesContainer
        .querySelectorAll<HTMLElement>('.js_pdp_dimension-selection__color-tile')
        .forEach(tile => resizeObserver.observe(tile));
    observers.push(resizeObserver);
}

/*                                                                                                 */
/*                                                                                              */
function collapsedLaneHidesTiles(lane: HTMLElement): boolean {
    const wasExpanded = !lane.classList.contains(collapsedContainerClass);
    if (wasExpanded) lane.classList.add(collapsedContainerClass);
    const hidesTiles = lane.scrollHeight > lane.clientHeight;
    if (wasExpanded) lane.classList.remove(collapsedContainerClass);
    return hidesTiles;
}

function ensureOcContainerStartsCollapsed(showMoreColorsLink: HTMLElement, imageTilesContainer: HTMLElement) {
    showMoreColorsLink.classList.remove(expandedLinkClass);
    showMoreColorsLink.classList.add(collapsedLinkClass);
    imageTilesContainer.classList.add(collapsedContainerClass);
}

function handleHorizontallyHiddenTilesForAllScrollingContainers(rootElement: HTMLElement, storageKey: string) {
    const scrollableContainer = rootElement.querySelectorAll<HTMLElement>(`.${selectionValuesForScrolling}`);
    scrollableContainer.forEach((container, index) => {
        const scopedStorageKey = `${storageKey}-${index}`;
        if (container.dataset.tileVariant === 'oc') {
            /*                                                                                                */
            requestAnimationFrame(() => handleHorizontallyHiddenTiles(container, scopedStorageKey));
            return;
        }
        handleHorizontallyHiddenTiles(container, scopedStorageKey);
    });
}

function handleHorizontallyHiddenTiles(tilesContainer: HTMLElement, storageKey: string) {
    if (tilesContainer.scrollWidth <= tilesContainer.clientWidth) return;

    /*                                                                                       */
    if (tilesContainer.dataset.dimScrollHandlersBound !== 'true') {
        tilesContainer.dataset.dimScrollHandlersBound = 'true';
        storeScrollPositionAfterScrolling(tilesContainer, storageKey);
        trackDimensionSwipes(tilesContainer);
    }
    restoreScrollPosition(tilesContainer, storageKey);
    scrollSelectedTileIntoView(tilesContainer);
}

export function getStorageKey(rootElement: HTMLElement) {
    const offerId = rootElement.getAttribute('data-offer-id');
    const productId = rootElement.getAttribute('data-product-id');

    return !!offerId ? offerId : productId;
}
